import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import {
  ArchiveCriteria,
  MONTH_NAMES,
  describeCriteria,
  downloadArchive,
  matchesArchiveCriteria
} from '../../services/archiveExport';
import { formatCurrency } from '../../utils/money';
import { BRAND } from '../../theme';

interface ArchiveDialogProps {
  open: boolean;
  onClose: () => void;
  /** Every saved load, all statuses */
  invoices: any[];
  years: number[];
  destinations: string[];
  /** Starts from whatever the report is currently showing */
  initialCriteria: ArchiveCriteria;
  onNotify: (message: string, severity?: 'success' | 'error' | 'info') => void;
}

const ArchiveDialog: React.FC<ArchiveDialogProps> = ({
  open,
  onClose,
  invoices,
  years,
  destinations,
  initialCriteria,
  onNotify
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [criteria, setCriteria] = useState<ArchiveCriteria>(initialCriteria);
  const [working, setWorking] = useState(false);

  // Each opening starts from the report's current view.
  useEffect(() => {
    if (open) setCriteria(initialCriteria);
  }, [open, initialCriteria]);

  const matching = useMemo(
    () => invoices.filter(invoice => matchesArchiveCriteria(invoice, criteria)),
    [invoices, criteria]
  );
  const matchingTotal = useMemo(
    () => matching.reduce((sum, invoice) => sum + (Number(invoice.totalCharges) || 0), 0),
    [matching]
  );

  const handleGenerate = async () => {
    setWorking(true);
    try {
      const count = await downloadArchive(invoices, criteria);
      onNotify(`Archive downloaded: ${count} load${count === 1 ? '' : 's'} (${describeCriteria(criteria)})`);
      onClose();
    } catch (error) {
      console.error('Error generating archive:', error);
      onNotify(
        `Error generating archive: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={working ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ArchiveOutlinedIcon sx={{ color: BRAND.blue }} />
        Generate for Archive
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Every load that matches, in any status, goes into the same Loads spreadsheet Data
          Entry generates. Nothing is changed or removed.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="archive-year">Year</InputLabel>
              <Select
                labelId="archive-year"
                label="Year"
                value={criteria.year === 'all' ? 'all' : String(criteria.year)}
                onChange={(e) =>
                  setCriteria(prev => ({
                    ...prev,
                    year: e.target.value === 'all' ? 'all' : Number(e.target.value)
                  }))
                }
              >
                <MenuItem value="all">All years</MenuItem>
                {years.map(year => (
                  <MenuItem key={year} value={String(year)}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="archive-month">Month</InputLabel>
              <Select
                labelId="archive-month"
                label="Month"
                value={criteria.month === 'all' ? 'all' : String(criteria.month)}
                onChange={(e) =>
                  setCriteria(prev => ({
                    ...prev,
                    month: e.target.value === 'all' ? 'all' : Number(e.target.value)
                  }))
                }
              >
                <MenuItem value="all">All months</MenuItem>
                {MONTH_NAMES.map((name, index) => (
                  <MenuItem key={name} value={String(index)}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Autocomplete
            freeSolo
            size="small"
            options={destinations}
            inputValue={criteria.destination}
            onInputChange={(_, value) => setCriteria(prev => ({ ...prev, destination: value }))}
            renderInput={(params) => (
              <TextField {...params} label="Destination" placeholder="Any destination" />
            )}
          />

          <Alert severity={matching.length ? 'info' : 'warning'} icon={false}>
            {matching.length ? (
              <>
                <strong>
                  {matching.length} load{matching.length === 1 ? '' : 's'}
                </strong>{' '}
                · {formatCurrency(matchingTotal)} · {describeCriteria(criteria)}
              </>
            ) : (
              'No loads match these filters.'
            )}
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={working} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleGenerate}
          disabled={working || matching.length === 0}
          startIcon={working ? <CircularProgress size={16} color="inherit" /> : <ArchiveOutlinedIcon />}
        >
          {working ? 'Generating...' : 'Download Excel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArchiveDialog;
